import { createFileRoute } from "@tanstack/react-router";
import { ProtectedShell } from "@/features/auth/ProtectedShell";
import { Panel } from "@/components/ui/Panel";

type Category = "feature" | "design" | "ux" | "a11y" | "platform" | "fix" | "security";
interface Item { category: Category; title: string; detail?: string; }
interface Release { version: string; date: string; headline: string; summary?: string; items: Item[]; }

const categoryLabel: Record<Category, string> = {
  feature: "Feature",
  design: "Design",
  ux: "UX",
  a11y: "Accessibility",
  platform: "Platform",
  fix: "Fix",
  security: "Security",
};

// New releases go at the top. Single source of truth — keep in sync with the
// product changelog.
const releases: Release[] = [
  {
    version: "0.6",
    date: "2026-05-22",
    headline: "React 19 SPA + Cloudflare Worker API",
    summary:
      "Whole-stack refactor. The Astro SSR app is now a Vite-built React 19 SPA fronting a Hono-on-Cloudflare-Workers API. Every page ported, every endpoint Worker-native, all four monorepo workspaces (legacy-astro, shared, worker, web) typecheck and build clean.",
    items: [
      { category: "platform", title: "Monorepo split: apps/web + apps/worker + packages/shared", detail: "Bun workspaces; legacy Astro relocated under apps/legacy-astro as the cutover rollback target." },
      { category: "feature", title: "Hono Worker API", detail: "Every page-level POST handler from Astro is now a real REST endpoint on the Worker: commissions lifecycle, payouts bulk, fx/products/reps CRUD, sync, reconcile, exports, search, impersonate, errors, cron." },
      { category: "feature", title: "TanStack Router + Query SPA", detail: "Type-safe routes, file-routed tree, ⌘K command palette, TanStack Query for cache + mutations + invalidation. Same URL contracts as legacy." },
      { category: "feature", title: "Cron scheduled() native trigger", detail: "Monthly statements fire via the Worker's scheduled() export — no HTTP round-trip. Manual bearer-token endpoint still works for ?dry=1 runs." },
      { category: "platform", title: "Two-job CI", detail: "deploy-worker → deploy-pages. Worker ships first so the SPA never lands without /api/* available; SPA goes to a preview alias until Phase 8 cutover." },
    ],
  },
  {
    version: "0.5",
    date: "2026-04-30",
    headline: "Approvals, multi-deal customers, onboarding tracking",
    summary:
      "Tighter financial controls and better state hygiene. New approval gate between earned and paid, reversible commissions with required reason, head-office customers that span multiple deals, and an onboarding-invoice status column so reps and admin can see at a glance whether onboarding has been raised, paid, or waived.",
    items: [
      { category: "feature", title: "Commission approval gate", detail: "New 'approved' status between earned and paid. Approval is optional — bulk mark-paid still works directly from earned. Audit: approved_at + approved_by." },
      { category: "feature", title: "Reverse a commission with reason", detail: "Detail page has a 'Reverse this commission' panel. Reason required; writes an offsetting row via reversal_of_id rather than editing history." },
      { category: "feature", title: "Onboarding products + invoice status", detail: "Mark Stripe products as ★ onboarding. Each invoice precomputes has_onboarding. Onboarding column surfaces status: not issued / issued / paid / free." },
      { category: "feature", title: "Free-onboarding waiver", detail: "Reps can mark their own deals as free onboarding from the dashboard (one-way). Admin clears the flag if set in error. Priority: paid > free > issued > not issued." },
      { category: "feature", title: "Head-office customers (multi-deal)", detail: "One Stripe customer can map to multiple HubSpot deals. With 2+ links, auto-cascade is suspended and per-invoice manual linking is mandatory." },
      { category: "feature", title: "Effective_to on commission structures", detail: "Optional end date when saving a structure so historical policies can be backfilled alongside the current open version." },
      { category: "fix", title: "Manual invoice link triggers commission", detail: "Manually linking an invoice to a deal now fires the commission calc when the invoice is already paid + commissionable." },
      { category: "fix", title: "Customer re-pairing reverses prior commissions", detail: "Re-pairing or unpairing a Stripe customer reverses the prior (deal, owner) commission before cascading invoices to the new deal." },
    ],
  },
  {
    version: "0.4",
    date: "2026-04-20",
    headline: "Product depth",
    summary:
      "Real workflow wins: bulk mark paid, KPI dashboard for admin, trend sparklines for reps, commission detail pages with editable notes, a reconciliation view, Slack notifications on commission earned, automated monthly statement emails, payroll-formatted CSV, GDPR anonymisation.",
    items: [
      { category: "feature", title: "Bulk mark paid", detail: "Finance + admin payouts have checkboxes, select-all, and a sticky action bar." },
      { category: "feature", title: "Admin KPI band", detail: "Reversal rate, average earned→paid time, aging > 30d total. Tells you where the process is leaking." },
      { category: "feature", title: "Trend sparkline + YoY delta", detail: "Inline SVG chart of earned by month over the last 13. Pill shows +/-X% vs same window last year." },
      { category: "feature", title: "Reconciliation view", detail: "Closed-won deals with no paid invoice, invoices without a deal link, paid invoices missing a commission row." },
      { category: "feature", title: "Payroll CSV export", detail: "Grouped by rep + salary_currency, cash-portion only, with FX conversion and BB-COMM reference." },
      { category: "feature", title: "Monthly rep statements", detail: "POST /api/cron/monthly-statements on the 1st emails every active rep their previous-month statement + YTD totals." },
      { category: "feature", title: "Slack notifications", detail: "Block Kit message on commission earned with amount, rep, deal name, invoice id." },
      { category: "security", title: "GDPR anonymisation", detail: "Anonymize action on reps page scrubs PII and bans the auth login while preserving the immutable commission history." },
    ],
  },
  {
    version: "0.3",
    date: "2026-04-20",
    headline: "Production hardening",
    summary:
      "A systematic pass across UX, accessibility, performance, and platform. Every destructive action guards behind a confirmation dialog; tables reflow for mobile; errors don't leak; ⌘K gets you anywhere.",
    items: [
      { category: "feature", title: "⌘K command palette", detail: "Fuzzy search across pages, reps, deals, invoices. Arrow keys + Enter + Escape. Role-scoped results." },
      { category: "feature", title: "CSV export for commissions", detail: "Download with filters applied. RFC-4180 quoting. Role-scoped (reps only see their own)." },
      { category: "feature", title: "Commission detail pages with timeline", detail: "Deal close → invoice paid → commission earned → marked paid + reversal chain." },
      { category: "feature", title: "Toast notifications + confirm dialogs", detail: "Replaces inline success/error panels. Every destructive action double-checks with contextual copy." },
      { category: "ux", title: "Mobile-responsive tables", detail: "Tables reflow into card rows below 640px." },
      { category: "ux", title: "Sanitized error copy", detail: "Raw Supabase/Stripe errors mapped to friendly copy. Originals logged server-side." },
      { category: "a11y", title: "Focus, contrast, semantics", detail: "Muted text bumped to gray600 (WCAG AA), DataTable scope attributes, dialog focus restoration, reduced-motion respected." },
      { category: "platform", title: "Self-hosted fonts + frontend error collector + CSP headers", detail: "Inter / DM Sans / JetBrains Mono / Permanent Marker from bundle. /api/errors sink. Tight CSP + HSTS." },
    ],
  },
  {
    version: "0.2",
    date: "2026-04-20",
    headline: "Bookboost design refresh",
    summary:
      "Complete visual overhaul moving from the initial dark-mode admin theme to an on-brand light theme matching bookboost.io.",
    items: [
      { category: "design", title: "Bookboost purple palette + typography swap", detail: "Untitled UI primary25–900. DM Sans for headings, Inter for body, JetBrains Mono for tabular data." },
      { category: "design", title: "Real Bookboost logo + soft purple wash", detail: "Inline SVG logo mark. Warm bg with subtle purple radial. Hairline borders on white panels." },
      { category: "design", title: "Refined metric cards + impersonation banner", detail: "Gradient brand accent on metric corner, tabular numerals, staggered entrance. Deep purple banner replaces mustard strip." },
    ],
  },
  {
    version: "0.1",
    date: "2026-04-20",
    headline: "Initial build",
    summary: "First pass of the commissions platform — schema, webhooks, dashboard, finance payout workflow.",
    items: [
      { category: "feature", title: "Dashboard + finance + admin", detail: "YTD earned/paid/outstanding. Pending + paid tabs. Rep management, deal/invoice/commission browsers." },
      { category: "feature", title: "HubSpot + Stripe webhook integration", detail: "Deals sync from HubSpot, invoices from Stripe. invoice.paid triggers commission against versioned commission_structures." },
      { category: "feature", title: "Split-equity structures + cap rollover", detail: "flat or split_equity. Versioned by effective_from/to. Once annual equity cap hit, remainder rolls to 100% cash." },
      { category: "security", title: "Row-level security + admin impersonation", detail: "Reps see only their own data via Postgres RLS. Admins can 'view as'." },
    ],
  },
];

function displayDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

const categoryStyle: Record<Category, React.CSSProperties> = {
  feature: { background: "var(--accent-2-soft)", color: "var(--accent-2)", borderColor: "color-mix(in srgb, var(--accent-2) 22%, transparent)" },
  design: { background: "var(--primary50)", color: "var(--accent-deep)", borderColor: "var(--primary200)" },
  ux: { background: "var(--gray100)", color: "var(--gray700)", borderColor: "var(--hairline)" },
  a11y: { background: "var(--primary100)", color: "var(--primary800)", borderColor: "var(--primary300)" },
  platform: { background: "var(--gray50)", color: "var(--gray700)", borderColor: "var(--hairline)" },
  fix: { background: "var(--warn-soft)", color: "var(--warn)", borderColor: "color-mix(in srgb, var(--warn) 26%, transparent)" },
  security: { background: "var(--err-soft)", color: "var(--err)", borderColor: "color-mix(in srgb, var(--err) 30%, transparent)" },
};

export const Route = createFileRoute("/changelog")({
  component: () => (
    <ProtectedShell>
      <ChangelogContent />
    </ProtectedShell>
  ),
});

function ChangelogContent() {
  return (
    <>
      <div style={{ maxWidth: 720, margin: "8px 0 36px" }}>
        <span
          aria-hidden
          style={{
            fontFamily: "var(--font-marker)",
            fontSize: 16,
            color: "var(--primary700)",
            display: "inline-block",
            transform: "rotate(-3deg)",
            letterSpacing: "0.02em",
            marginBottom: 8,
          }}
        >
          what&apos;s new
        </span>
        <h1 style={{ margin: "0 0 12px" }}>Changelog</h1>
        <p className="lede" style={{ maxWidth: 640 }}>
          A running list of what&apos;s shipped to the Commissions platform. New releases go at the top.
        </p>
      </div>

      {releases.map((release, idx) => (
        <section
          key={release.version}
          aria-labelledby={`v-${release.version}`}
          style={{
            display: "grid",
            gridTemplateColumns: "180px 1fr",
            gap: 40,
            padding: "28px 0 32px",
            borderTop: idx === 0 ? "1px solid var(--gray300)" : "1px solid var(--hairline)",
          }}
        >
          <header
            style={{
              position: "sticky",
              top: 80,
              alignSelf: "start",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span className="muted" style={{ fontSize: 13 }}>v</span>
              <span style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 600, letterSpacing: "-0.02em" }}>
                {release.version}
              </span>
            </div>
            <time
              dateTime={release.date}
              className="muted"
              style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}
            >
              {displayDate(release.date)}
            </time>
            {idx === 0 && (
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "var(--primary600)",
                  color: "#fff",
                  alignSelf: "flex-start",
                }}
              >
                Latest
              </span>
            )}
          </header>

          <div>
            <h2 id={`v-${release.version}`} style={{ marginTop: 0 }}>
              {release.headline}
            </h2>
            {release.summary && <p className="muted" style={{ marginBottom: 18 }}>{release.summary}</p>}

            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
              {release.items.map((item, i) => (
                <li
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: 14,
                    alignItems: "baseline",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      justifyContent: "center",
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: 10.5,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      border: "1px solid",
                      width: "fit-content",
                      ...categoryStyle[item.category],
                    }}
                  >
                    {categoryLabel[item.category]}
                  </span>
                  <div>
                    <span style={{ fontWeight: 500 }}>{item.title}</span>
                    {item.detail && (
                      <span className="muted" style={{ display: "block", marginTop: 4, fontSize: 13, lineHeight: 1.55 }}>
                        {item.detail}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      <Panel>
        <p style={{ margin: 0 }}>
          <strong>Something broken or missing?</strong> Ping Dylan directly. We&apos;ll add it to the next release.
        </p>
      </Panel>
    </>
  );
}
